import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "../AuthContext.tsx";
import { liveMatchService, calendarService } from "../api/service.ts";
import { useMatchReplay } from "./LiveMatch/replay/useMatchReplay.ts";
import ReplayScoreBoard from "./LiveMatch/replay/ReplayScoreBoard.tsx";
import VolleyballFieldView from "./LiveMatch/replay/VolleyballFieldView.tsx";
import MatchGallery from "./Home/MatchGallery.tsx";
import ArchiveDialog from "./Home/ArchiveDialog.tsx";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import type { NextMatchInfo } from "../api/types";

const POLL_INTERVAL = 10_000;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}


const LIVE_POLL_INTERVAL = 5_000;

// Pannello live: mostra tabellone + campo per un eventId attivo
function LivePanel({ eventId }: { eventId: number }) {
  const replay = useMatchReplay(eventId, true);
  const reloadRef = useRef(replay.reload);
  reloadRef.current = replay.reload;

  // Polling ogni 5s per aggiornare il log dal backend
  useEffect(() => {
    const id = setInterval(() => reloadRef.current(), LIVE_POLL_INTERVAL);
    return () => clearInterval(id);
  }, []);

  const snap = replay.currentSnapshot;
  const swapped = replay.ourTeamSide === "AWAY";
  const displayHome = swapped ? replay.awayTeamName : replay.homeTeamName;
  const displayAway = swapped ? replay.homeTeamName : replay.awayTeamName;

  if (replay.loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  if (!snap) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
        <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>
          Partita in corso — in attesa del primo punto…
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ReplayScoreBoard
        homeTeamName={displayHome}
        awayTeamName={displayAway}
        homePoints={snap.homePoints}
        awayPoints={snap.awayPoints}
        setsWon={snap.setsWon}
        completedSets={snap.completedSets}
        currentSet={snap.currentSet}
        servingTeam={snap.servingTeamNow}
        swapped={swapped}
      />
      <Box sx={{ flex: 1, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <VolleyballFieldView
          homeLineup={snap.homeLineup}
          awayLineup={snap.awayLineup}
          homeCallup={replay.homeCallup}
          awayCallup={replay.awayCallup}
          homeShirtColor={replay.homeShirtColor}
          awayShirtColor={replay.awayShirtColor}
          homeLiberoId={replay.homeLiberoId}
          awayLiberoId={replay.awayLiberoId}
          homeTeamName={displayHome}
          awayTeamName={displayAway}
          swapped={swapped}
        />
      </Box>
    </>
  );
}

export default function GuestView() {
  const { logout } = useAuth();
  const [activeEventId, setActiveEventId] = useState<number | null>(null);
  const [nextMatch, setNextMatch] = useState<NextMatchInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const checkStatus = useCallback(async () => {
    const session = await liveMatchService.getActive();
    if (session) {
      setActiveEventId(session.matchEventId);
      return;
    }
    setActiveEventId(null);
    const next = await calendarService.getNextMatch();
    setNextMatch(next);
  }, []);

  useEffect(() => {
    checkStatus().finally(() => setLoading(false));
    const id = setInterval(checkStatus, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [checkStatus]);

  const isLive = activeEventId !== null;

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#0d0d1a",
        color: "white",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          bgcolor: "primary.main",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box component="img" src={`${process.env.PUBLIC_URL}/vb-logo.png`} alt="VB Volley" sx={{ width: 32, height: 32 }} />
          <Typography variant="subtitle1" fontWeight={700}>
            Vellezzo B. Volley
          </Typography>
        </Box>
        {isLive && (
          <Box
            sx={{
              px: 1.5,
              py: 0.25,
              bgcolor: "error.main",
              borderRadius: 1,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 1,
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%,100%": { opacity: 1 },
                "50%": { opacity: 0.5 },
              },
            }}
          >
            LIVE
          </Box>
        )}
        <Button size="small" color="inherit" onClick={logout} sx={{ opacity: 0.7 }}>
          Esci
        </Button>
      </Box>

      {/* Corpo */}
      {loading ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress sx={{ color: "white" }} />
        </Box>
      ) : isLive ? (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <LivePanel eventId={activeEventId!} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 3 }}>
          <Box sx={{ maxWidth: 720, mx: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: "rgba(255,255,255,0.7)" }}>
                Nessuna partita in corso
              </Typography>
              {nextMatch ? (
                <>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mt: 2 }}>
                    Prossimo incontro
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
                    {nextMatch.homeTeam}
                    <Box component="span" sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 400, mx: 1 }}>vs</Box>
                    {nextMatch.awayTeam}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "primary.light", mt: 1, textTransform: "capitalize" }}>
                    {formatDate(nextMatch.date)}{nextMatch.time ? ` · ore ${nextMatch.time.substring(0, 5)}` : ""}
                  </Typography>
                  {nextMatch.location && (
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>{nextMatch.location}</Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", mt: 1 }}>
                  Nessun incontro in programma
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)" }}>
                Le nostre partite
              </Typography>
              <MatchGallery showDetails={false} />
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button variant="outlined" color="inherit" startIcon={<PhotoLibraryIcon />} onClick={() => setArchiveOpen(true)}
                  sx={{ borderColor: "rgba(255,255,255,0.4)" }}>
                  Archivio storico
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <ArchiveDialog open={archiveOpen} onClose={() => setArchiveOpen(false)} />
    </Box>
  );
}
